/**
 * Shell Application - MicroFrontend Loader
 * Project: Quantiix Shell (quantiix-shell)
 * Module: src/micro-frontends/MicroFrontendLoader.jsx
 *
 * Migrated from manual container API (init/get) to @module-federation/runtime.
 * Uses registerRemotes + loadRemote for dynamic MFE loading.
 */

import { Suspense, useState, useEffect } from 'react';
import { registerRemotes, loadRemote } from '@module-federation/runtime';
import ErrorBoundary from '../components/ErrorBoundary';
import { PageLoader } from '../components/PageLoader';
import { useAuth } from '../contexts/AuthContext';
import { useSSE } from '../contexts/SSEContext';
import { MICRO_FRONTENDS } from '../config/MicroFrontend';
import { createLogger } from '../utils/logger';

const logger = createLogger('Shell::MicroFrontendLoader');

// Track which remotes have been registered to avoid duplicate registration
const registeredRemotes = new Set();
// remoteName → promise that resolves once its CSS has loaded (cached so concurrent
// mounts share one load).
const cssPromises = new Map();

/**
 * Register a remote MFE with the Module Federation runtime.
 * Only registers once per remote name.
 */
function ensureRemoteRegistered(config) {
    const remoteName = config.scope;

    if (registeredRemotes.has(remoteName)) {
        logger.debug(`Remote "${remoteName}" already registered`);
        return;
    }

    logger.info(`Registering remote "${remoteName}" at ${config.url}`);

    registerRemotes([
        {
            name: remoteName,
            entry: config.url,
            type: 'module',
        }
    ]);

    registeredRemotes.add(remoteName);
}

// Origin the remote MFEs are pulled from in local dev (injected at build time; empty in
// production, where remotes are same-origin). A direct cross-origin fetch of a remote's
// `.vite/manifest.json` is CORS-blocked (the CDN sends no Access-Control-Allow-Origin on
// it), so for CDN remotes we read the manifest through the dev/preview server's same-origin
// `/__mfe_cdn__` proxy. The stylesheet `<link>` itself still uses the absolute CDN URL —
// cross-origin stylesheet links don't need CORS.
const CDN_ORIGIN = import.meta.env.VITE_MFE_CDN_ORIGIN || '';

function manifestUrlFor(baseUrl) {
    const direct = `${baseUrl}.vite/manifest.json`;
    if (!CDN_ORIGIN) return direct;
    const u = new URL(direct, window.location.href);
    return u.origin === CDN_ORIGIN ? `/__mfe_cdn__${u.pathname}${u.search}` : direct;
}

/**
 * Load the remote MFE's CSS.
 *
 * Resolves the *content-hashed* CSS filename(s) from the remote's Vite build
 * manifest (`.vite/manifest.json`) instead of hardcoding a fixed name — a fixed
 * `assets/style.css` served `immutable` is a cache trap (the CDN keeps the old CSS
 * while the hashed JS updates → class-hash mismatch → styles silently stop applying).
 * Falls back to the legacy `assets/style.css` whenever the manifest can't be read —
 * either a *reachable* host with no manifest (404), or a *blocked* fetch (typically a
 * cross-origin shell pulling a remote from the CDN with no CORS header on the manifest).
 * The legacy name still works for any remote that actually publishes `assets/style.css`;
 * for a content-hashed remote it won't resolve, so on a *blocked* fetch we also log a
 * clear warning pointing at the CORS gap (the real fix is CORS on the manifest).
 *
 * Returns a promise that resolves once the stylesheet(s) have loaded, so the caller
 * can apply them before first paint. Cached per remote.
 */
function loadRemoteCSS(config) {
    const remoteName = config.scope;
    if (cssPromises.has(remoteName)) return cssPromises.get(remoteName);

    const baseUrl = config.url.substring(0, config.url.lastIndexOf('/') + 1);

    const promise = (async () => {
        let cssFiles = [];
        let manifestBlocked = false;
        try {
            const res = await fetch(manifestUrlFor(baseUrl), { cache: 'no-cache' });
            if (res.ok) {
                const manifest = await res.json();
                const set = new Set();
                for (const key of Object.keys(manifest)) {
                    const entry = manifest[key];
                    (entry?.css || []).forEach((c) => set.add(c));
                    if (entry?.file?.endsWith('.css')) set.add(entry.file);
                }
                cssFiles = [...set];
            }
            // A reachable-but-missing manifest (404) resolves with res.ok === false and
            // falls through to the legacy fixed-name fallback below.
        } catch {
            // fetch() *rejected* — the manifest is unreadable, not merely absent. The
            // usual cause is a cross-origin shell (local dev pulling a remote from the
            // CDN) with no `Access-Control-Allow-Origin` on `.vite/manifest.json`. We
            // can't discover the hashed CSS name, and the legacy fixed name no longer
            // exists on the CDN (it resolves to the SPA index.html, served text/html),
            // so injecting it just yields a refused, mis-typed stylesheet.
            manifestBlocked = true;
        }

        if (cssFiles.length === 0) {
            if (manifestBlocked) {
                logger.warn(
                    `Could not read the build manifest for remote "${remoteName}" ` +
                    `(${baseUrl}.vite/manifest.json) — usually a cross-origin shell (local dev ` +
                    `pulling from the CDN) with no "Access-Control-Allow-Origin" on the manifest. ` +
                    `Falling back to the legacy "assets/style.css"; a content-hashed remote will ` +
                    `stay unstyled until the host sends CORS on the manifest (or you run it locally).`
                );
            }
            // No manifest (404) or unreadable (CORS): fall back to the historical fixed name.
            // Still works for any remote that publishes assets/style.css.
            cssFiles = ['assets/style.css'];
        }

        logger.info(`Loading CSS for "${remoteName}": ${cssFiles.join(', ')}`);

        cssFiles.forEach((file) => {
            const href = `${baseUrl}${file}`;
            if (document.querySelector(`link[rel="stylesheet"][data-mfe="${remoteName}"][href="${href}"]`)) return;
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.dataset.mfe = remoteName;
            // Some remotes ship no own stylesheet (all their styling is design-system
            // CSS, which the shell loads once). A missing file is expected — don't surface it.
            link.onerror = () => logger.debug(`No own stylesheet for "${remoteName}" at ${href} (design-system CSS is provided by the shell)`);
            document.head.appendChild(link);
        });
    })();

    cssPromises.set(remoteName, promise);
    return promise;
}

function MicroFrontendLoader({ appId, subPath = '', mode = 'default' }) {
    const authContext = useAuth();
    const { hasPermission, token, authUtils, user, permissions } = authContext;
    const sseContext = useSSE();

    const [MicroFrontend, setMicroFrontend] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const config = MICRO_FRONTENDS[appId];

    useEffect(() => {
        logger.info(`MicroFrontendLoader - appId: ${appId}, subPath: ${subPath}, mode: ${mode}`);
    }, [appId, subPath, mode]);

    useEffect(() => {
        setLoading(true);
        setMicroFrontend(null);
        setError(null);

        if (!config) {
            setError(`Micro-frontend "${appId}" not found`);
            setLoading(false);
            return;
        }

        // Check if user has permission to access this micro-frontend.
        // Two gating models:
        //   - requiredPermissions (array): role-based any-of (hasAnyPermission)
        //   - requiredPermission  (string, legacy): feature-flag check (hasFeature)
        const { hasFeature, hasAnyPermission } = authContext;
        const isAllowed = (() => {
            if (Array.isArray(config.requiredPermissions) && config.requiredPermissions.length > 0) {
                return hasAnyPermission(config.requiredPermissions);
            }
            return hasFeature(config.requiredPermission);
        })();
        if (mode !== 'operator' && !isAllowed) {
            setError(`You don't have permission to access this application`);
            setLoading(false);
            return;
        }

        const loadRemoteModule = async () => {
            try {
                // Step 1: Register the remote (idempotent)
                ensureRemoteRegistered(config);

                // Step 2: Start loading the remote's CSS (in parallel with the JS).
                // Awaited before mount (below) so the panel doesn't paint unstyled.
                const cssReady = loadRemoteCSS(config);

                // Step 3: Load the exposed module
                // Format: "remoteName/exposedModule" e.g. "dashboard/App"
                const exposePath = config.module.replace('./', '');
                const remoteId = `${config.scope}/${exposePath}`;

                logger.info(`Loading remote module: ${remoteId}`);

                const componentModule = await loadRemote(remoteId);

                logger.info('Remote module loaded:', typeof componentModule, Object.keys(componentModule || {}));

                const Component = componentModule?.default || componentModule?.App || componentModule;

                if (!Component) {
                    throw new Error(`Component not found in module. Available keys: ${Object.keys(componentModule || {})}`);
                }

                if (typeof Component !== 'function') {
                    throw new Error(`Component is not a function. Got: ${typeof Component}`);
                }

                logger.info('Component loaded successfully:', typeof Component);

                // Wrapper component with auth context
                // Note: i18n is no longer passed as a prop — MFEs consume the shared
                // i18n instance directly via Module Federation ('shell_app/i18n').
                const WrappedComponent = (props) => {
                    return <Component
                        {...props}

                        // Legacy prop (backward compatibility)
                        token={token}

                        // New auth object with utilities
                        auth={{
                            ...authUtils,
                            user,
                            logout: authContext.logout
                        }}

                        // App context
                        appId={appId}
                        subPath={subPath}
                        mode={mode}

                        // SSE context
                        sse={{
                            connected: sseContext.connected,
                            registerHandler: sseContext.registerHandler,
                            subscribe: sseContext.subscribe || sseContext.registerHandler,
                            generateMessageId: sseContext.generateMessageId,
                            clientId: sseContext.clientId
                        }}
                    />;
                };

                // Ensure the remote's stylesheet is applied before first paint so the
                // panel never renders unstyled (also avoids a CSS-after-mount race).
                await cssReady;

                setMicroFrontend(() => WrappedComponent);
                setError(null);

            } catch (err) {
                logger.error(`Error loading micro-frontend ${appId}:`, err);
                setError(`Failed to load micro-frontend: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        loadRemoteModule();

    }, [appId, hasPermission, token, subPath, mode, authUtils]);

    if (loading) {
        return <PageLoader />;
    }

    if (error) {
        return (
            <div className="error-container" style={{
                padding: '20px',
                textAlign: 'center',
                color: 'red'
            }}>
                <h3>Error Loading Application</h3>
                <p>{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        marginTop: '10px',
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!MicroFrontend) {
        return (
            <div className="error-container" style={{
                padding: '20px',
                textAlign: 'center'
            }}>
                <p>Failed to load micro-frontend</p>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <Suspense fallback={<PageLoader fullPage={false} />}>
                <MicroFrontend />
            </Suspense>
        </ErrorBoundary>
    );
}

export default MicroFrontendLoader;